import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { RedditPixelRouteTracker } from './lib/redditPixel';
import { GoogleAdsConversionTracker } from './lib/googleAds';
import { PageHeader } from './components/PageHeader';
import { Toaster } from './components/ui/sonner';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PageLoader } from './components/PageLoader';
import { SubscriptionProvider } from './hooks/useSubscription';
import { Register } from './pages/Register';
import { VerifyEmail } from './pages/VerifyEmail';
import { Login } from './pages/Login';
import { RequestPasswordReset } from './pages/RequestPasswordReset';
import { ResetPassword } from './pages/ResetPassword';
import { TenantSetup } from './pages/TenantSetup';
import { SetupWizard } from './pages/SetupWizard';
import { Home } from './pages/Home';
import IntegrationsIndex from './pages/IntegrationsIndex';
import { useAuth } from './hooks/useAuth';
import { TopologyMapExpansion as TopologyMap } from './pages/TopologyMap/TopologyMapExpansion';
import { TrafficInsights } from './pages/TrafficInsights';
import IncidentResponse from './pages/IncidentResponse';
import IncidentDetail from './pages/IncidentDetail';
import ClusterIncidentGroup from './pages/ClusterIncidentGroup';
import CloudIncidentResponse from './pages/CloudIncidentResponse';
import CloudIncidentDetail from './pages/CloudIncidentDetail';
import Inventory from './pages/Inventory';
import { KubernetesChat } from './pages/KubernetesChat';
import { KubernetesRiskAssessment } from './pages/KubernetesRiskAssessment';
import KubernetesRiskAssessmentDetail from './pages/KubernetesRiskAssessmentDetail';
import RiskAssessmentFindingDetail from './pages/RiskAssessmentFindingDetail';
import VulnerabilityDashboard from './pages/VulnerabilityDashboard';
import GenerateOperatorToken from './pages/GenerateOperatorToken';
import Settings from './pages/Settings';
import TeamManagement from './pages/TeamManagement';
import CICDIntegration from './pages/CICDIntegration';
import AuditLog from './pages/AuditLog';
import ChatChangelog from './pages/ChatChangelog';
import { CloudIntegrations } from './pages/CloudIntegrations';
import { EKSClusterProgress } from './pages/EKSClusterProgress';
import SlackIntegration from './pages/SlackIntegration';
import SlackCallback from './pages/SlackCallback';
import PagerDutyIntegration from './pages/PagerDutyIntegration';
import PostHogIntegration from './pages/PostHogIntegration';
import VercelIntegration from './pages/VercelIntegration';
import RailwayIntegration from './pages/RailwayIntegration';
import FlyioIntegration from './pages/FlyioIntegration';
import NebiusIntegration from './pages/NebiusIntegration';
import BeamIntegration from './pages/BeamIntegration';
import DaytonaIntegration from './pages/DaytonaIntegration';
import SupabaseIntegration from './pages/SupabaseIntegration';
import PlanetScaleIntegration from './pages/PlanetScaleIntegration';
import NeonIntegration from './pages/NeonIntegration';
import ClickHouseIntegration from './pages/ClickHouseIntegration';
import JenkinsIntegration from './pages/JenkinsIntegration';
import CircleCIIntegration from './pages/CircleCIIntegration';
import SonarCloudIntegration from './pages/SonarCloudIntegration';
import OktaIntegration from './pages/OktaIntegration';
import DatabricksIntegration from './pages/DatabricksIntegration';
import TerraformIntegration from './pages/TerraformIntegration';
import PulumiIntegration from './pages/PulumiIntegration';
import VaultIntegration from './pages/VaultIntegration';
import InfisicalIntegration from './pages/InfisicalIntegration';
import ArgoCDIntegration from './pages/ArgoCDIntegration';
import ArgoRolloutsIntegration from './pages/ArgoRolloutsIntegration';
import FluxCDIntegration from './pages/FluxCDIntegration';
import KarpenterIntegration from './pages/KarpenterIntegration';
import KyvernoIntegration from './pages/KyvernoIntegration';
import TrivyIntegration from './pages/TrivyIntegration';
import DatadogIntegration from './pages/DatadogIntegration';
import CloudflareIntegration from './pages/CloudflareIntegration';
import TribalKnowledgeSettings from './pages/TribalKnowledgeSettings';
import OnPremDeployment from './pages/OnPremDeployment';
import OAuthCallback from './components/auth/OAuthCallback';
import { GoogleCallback } from './pages/GoogleCallback';
import { GitHubCallback } from './pages/GitHubCallback';
import Billing from './pages/Billing';
import WorkflowList from './pages/WorkflowList';
import WorkflowBuilder from './pages/WorkflowBuilder';
import WorkflowDetail from './pages/WorkflowDetail';
import WorkflowExecutionDetail from './pages/WorkflowExecutionDetail';
import WorkflowRCADetail from './pages/WorkflowRCADetail';
import WorkflowApprovals from './pages/WorkflowApprovals';
import WorkflowSuggestions from './pages/WorkflowSuggestions';
import WorkflowRequests from './pages/WorkflowRequests';
import WorkflowRequestChat from './pages/WorkflowRequestChat';
import WorkflowExecutions from './pages/WorkflowExecutions';
import WorkflowObservability from './pages/WorkflowObservability';
import APIKeys from './pages/APIKeys';
// Policy generation UI removed - imports commented out
// DISABLED: Policy generation pipeline not in use - keeping imports commented for easy rollback
// import { NetworkPolicyDetail } from './pages/NetworkPolicyDetail';
// import { NetworkPolicyEffectivenessDetail } from './pages/NetworkPolicyEffectivenessDetail';
// import { AuthorizationPolicyDetail } from './pages/AuthorizationPolicyDetail';
// import { AuthorizationPolicyEffectivenessDetail } from './pages/AuthorizationPolicyEffectivenessDetail';
// import { SecurityRecommendations } from './pages/SecurityRecommendations';
// import { SecurityEffectiveness } from './pages/SecurityEffectiveness';
import { ADMIN_ROLES, ALL_ROLES } from './utils/roles';
import { apiClient } from './api/client';
import { IS_DEMO_MODE } from './lib/demoMode';
import { ThemeProvider } from './components/theme-provider';
import { useMobile } from './hooks/useMobile';
import { SidebarContext } from './hooks/useSidebar';
import { Sheet, SheetContent } from './components/ui/sheet';
import { cn } from './lib/utils';

// Pages that are accessible without an active subscription
const SUBSCRIPTION_EXEMPT_PATHS = ['/billing', '/home', '/settings', '/integrations', '/cloud-integrations', '/team-management', '/topology-map', '/inventory', '/audit-log', '/onboard-cluster', '/cicd-integration'];

// Legacy products (Incidents, Cloud AI Copilot) are hidden unless the tenant
// has been re-enabled via tenants.legacy_products_enabled. Their routes
// redirect home so deep links don't expose the hidden pages.
function LegacyProductGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading || !user) {
    return <PageLoader />;
  }
  if (!user.legacy_products_enabled) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobile();
  const [isValidating, setIsValidating] = useState(true);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop sidebar collapse (icons-only rail); persisted across sessions.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    () => typeof window !== 'undefined' && localStorage.getItem('kestrel-sidebar-collapsed') === '1'
  );
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('kestrel-sidebar-collapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    const validate = async () => {
      const isValid = await apiClient.validateSession();
      if (!isValid) {
        navigate('/login');
        return;
      }

      // New org_admins who haven't finished (or skipped) the setup wizard are
      // sent there instead of the dashboard. Checked here, while the bare
      // validation spinner is still up, so the sidebar/header never flash
      // before the redirect. window.location is used intentionally: this runs
      // once on mount and should reflect the initially loaded path.
      if (window.location.pathname === '/home') {
        try {
          const [user, setupState] = await Promise.all([
            apiClient.getCurrentUser(),
            apiClient.getSetupWizardState(),
          ]);
          if (user.role === 'org_admin' && !setupState.completed) {
            navigate('/setup', { replace: true });
            return;
          }
        } catch {
          // On error, fall through to the dashboard.
        }
      }

      // Check subscription status (SaaS only — on-prem uses license enforcement)
      try {
        const status = await apiClient.getSubscriptionStatus();
        setSubscriptionExpired(!status.has_active_subscription);
      } catch {
        // If the check fails (e.g. Stripe not configured / on-prem), don't block
      }

      setIsValidating(false);
    };
    validate();
  }, [navigate]);

  // Listen for subscription changes (e.g. free trial started, checkout completed)
  // so the layout gate updates without requiring a full page reload.
  useEffect(() => {
    const handleSubscriptionChanged = async () => {
      try {
        const status = await apiClient.getSubscriptionStatus();
        setSubscriptionExpired(!status.has_active_subscription);
      } catch {
        // Don't block on failure
      }
    };
    window.addEventListener('subscription-changed', handleSubscriptionChanged);
    return () => window.removeEventListener('subscription-changed', handleSubscriptionChanged);
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  if (isValidating) {
    // Position the spinner exactly where page-level spinners render once the
    // chrome mounts (offset for the sidebar and header). Otherwise the spinner
    // visibly jumps from viewport-center to content-center on login, which
    // reads as multiple spinners.
    return (
      <div className="min-h-screen bg-background flex">
        <div className={cn('flex-1 flex flex-col min-w-0', !isMobile && 'ml-64')}>
          {/* PageHeader placeholder (py-4 + title + description) */}
          <div className="h-[88px]" aria-hidden />
          <main className="flex-1">
            <div className="px-4 sm:px-6 lg:px-8 py-4">
              <PageLoader />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // If subscription expired and user is on a non-exempt page, redirect to billing
  const isExemptPage = SUBSCRIPTION_EXEMPT_PATHS.some(p => location.pathname.startsWith(p));
  if (subscriptionExpired && !isExemptPage) {
    return <Navigate to="/billing" replace />;
  }

  const isChatPage = location.pathname === '/cloud-ai-copilot';
  const isWorkflowBuilder = location.pathname === '/workflows/new' || (location.pathname.startsWith('/workflows/') && location.pathname.endsWith('/edit'));
  const isWorkflowSuggestions = location.pathname === '/workflows/suggestions';
  const isWorkflowRequestChat = location.pathname === '/workflows/request';
  const isFullHeightPage = isChatPage || isWorkflowBuilder || isWorkflowSuggestions || isWorkflowRequestChat;

  return (
    <SubscriptionProvider>
    <SidebarContext.Provider value={{ openSidebar: () => setSidebarOpen(true), isMobile }}>
      <div className={cn("bg-background flex", isFullHeightPage ? "h-screen" : "min-h-screen")}>
        {isMobile ? (
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent side="left" className="p-0 w-72">
              <Sidebar isDrawer onNavigate={() => setSidebarOpen(false)} />
            </SheetContent>
          </Sheet>
        ) : (
          <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebarCollapsed} />
        )}
        <div className={cn("flex-1 flex flex-col min-w-0", !isMobile && cn("transition-[margin] duration-[288ms] ease-in-out", sidebarCollapsed ? "ml-16" : "ml-64"), isFullHeightPage && "overflow-hidden")}>
          {!isFullHeightPage && (
            <PageHeader
              onMenuClick={() => setSidebarOpen(true)}
              showMenuButton={isMobile}
            />
          )}
          <main className={cn("flex-1", isFullHeightPage ? "overflow-hidden" : "overflow-auto")}>
            <div className={isFullHeightPage ? "h-full" : "px-4 sm:px-6 lg:px-8 py-4 max-w-full"}>
              {children}
            </div>
          </main>
        </div>
      </div>
    </SidebarContext.Provider>
    </SubscriptionProvider>
  );
}

// Minimal chrome for the public demo builder: no platform sidebar or page
// header — the builder draws its own header — but the SidebarContext the
// builder's MobileMenuButton expects is still provided (as a no-op; there is
// no sidebar to open, so the mobile menu button never renders).
function DemoBuilderLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarContext.Provider value={{ openSidebar: () => {}, isMobile: false }}>
      <div className="bg-background flex h-screen">
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <main className="flex-1 overflow-hidden">
            <div className="h-full">{children}</div>
          </main>
        </div>
      </div>
    </SidebarContext.Provider>
  );
}

// Short shareable URLs (demo.usekestrel.ai/w/{id}) → builder with the shared
// snapshot loaded.
function DemoSharedWorkflowRedirect() {
  const { shareId } = useParams<{ shareId: string }>();
  return <Navigate to={`/workflows/new?simulated=1&share=${encodeURIComponent(shareId || '')}`} replace />;
}

// Bundle URLs (demo.usekestrel.ai/b/{id}) → builder with several shared
// snapshots loaded behind a workflow picker.
function DemoSharedBundleRedirect() {
  const { bundleId } = useParams<{ bundleId: string }>();
  return <Navigate to={`/workflows/new?simulated=1&bundle=${encodeURIComponent(bundleId || '')}`} replace />;
}

function App() {
  // Public demo builder (demo.usekestrel.ai): only the Workflow Builder is
  // mounted, without the authenticated layout, sidebar chrome, or session
  // validation. Everything else redirects into the builder.
  if (IS_DEMO_MODE) {
    return (
      <ThemeProvider defaultTheme="system" storageKey="autonp-ui-theme">
        <Toaster />
        <Router>
          <Routes>
            <Route
              path="/workflows/new"
              element={
                <DemoBuilderLayout>
                  <WorkflowBuilder />
                </DemoBuilderLayout>
              }
            />
            <Route path="/w/:shareId" element={<DemoSharedWorkflowRedirect />} />
            <Route path="/b/:bundleId" element={<DemoSharedBundleRedirect />} />
            <Route path="*" element={<Navigate to="/workflows/new?simulated=1" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider defaultTheme="system" storageKey="autonp-ui-theme">
      <Toaster />
      <Router>
        <RedditPixelRouteTracker />
        <GoogleAdsConversionTracker />
        <Routes>
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<OAuthCallback />} />
          <Route path="/slack/callback" element={<SlackCallback />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/auth/github/callback" element={<GitHubCallback />} />
          <Route path="/request-password-reset" element={<RequestPasswordReset />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/tenant-setup" element={<TenantSetup />} />
          <Route path="/setup" element={<SetupWizard />} />
          <Route
            path="/home"
            element={
              <AuthenticatedLayout>
                <Home />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/topology-map"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <TopologyMap />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/traffic-insights"
            element={
              <AuthenticatedLayout>
                <TrafficInsights />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/incidents"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <IncidentResponse />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/incidents/cluster/:clusterId"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <ClusterIncidentGroup />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/incidents/:incidentId"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <IncidentDetail />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/cloud-incidents"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <CloudIncidentResponse />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/cloud-incidents/:incidentId"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <CloudIncidentDetail />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/inventory"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <Inventory />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/cloud-ai-copilot"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <KubernetesChat />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/chat-changelog"
            element={
              <AuthenticatedLayout>
                <LegacyProductGuard>
                  <ChatChangelog />
                </LegacyProductGuard>
              </AuthenticatedLayout>
            }
          />
          {/* Policy generation UI removed - routes commented out
          <Route
          {/* DISABLED: Policy generation pipeline not in use - keeping routes commented for easy rollback */}
          {/* <Route
            path="/security-recommendations"
            element={
              <AuthenticatedLayout>
                <SecurityRecommendations />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/network-policies/:id"
            element={
              <AuthenticatedLayout>
                <NetworkPolicyDetail />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/authorization-policies/:id"
            element={
              <AuthenticatedLayout>
                <AuthorizationPolicyDetail />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/security-effectiveness"
            element={
              <AuthenticatedLayout>
                <SecurityEffectiveness />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/network-policy-effectiveness/:id"
            element={
              <AuthenticatedLayout>
                <NetworkPolicyEffectivenessDetail />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/authorization-policy-effectiveness/:id"
            element={
              <AuthenticatedLayout>
                <AuthorizationPolicyEffectivenessDetail />
              </AuthenticatedLayout>
            }
          />
          */}
          <Route
            path="/risk-assessment"
            element={
              <AuthenticatedLayout>
                <KubernetesRiskAssessment />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/risk-assessment/:assessmentId"
            element={
              <AuthenticatedLayout>
                <KubernetesRiskAssessmentDetail />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/risk-assessment/:assessmentId/findings/:findingId"
            element={
              <AuthenticatedLayout>
                <RiskAssessmentFindingDetail />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/findings/:findingId"
            element={
              <AuthenticatedLayout>
                <RiskAssessmentFindingDetail />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/cloud-findings/:findingId"
            element={
              <AuthenticatedLayout>
                <RiskAssessmentFindingDetail />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/vulnerability-dashboard"
            element={
              <AuthenticatedLayout>
                <VulnerabilityDashboard />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/onboard-cluster"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <GenerateOperatorToken />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/settings"
            element={
              <AuthenticatedLayout>
                <Settings />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/on-prem"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <OnPremDeployment />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/team-management"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={['org_admin']}>
                  <TeamManagement />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/cicd-integration"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <CICDIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations"
            element={
              <AuthenticatedLayout>
                <IntegrationsIndex />
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/slack"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <SlackIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/pagerduty"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <PagerDutyIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/posthog"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <PostHogIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/vercel"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <VercelIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/railway"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <RailwayIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/flyio"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <FlyioIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/nebius"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <NebiusIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/beam"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <BeamIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/daytona"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <DaytonaIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/supabase"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <SupabaseIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/planetscale"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <PlanetScaleIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/neon"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <NeonIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/clickhouse"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <ClickHouseIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/jenkins"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <JenkinsIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/circleci"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <CircleCIIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/sonarcloud"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <SonarCloudIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/okta"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <OktaIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/databricks"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <DatabricksIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/terraform"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <TerraformIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/pulumi"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <PulumiIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/vault"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <VaultIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/infisical"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <InfisicalIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/argocd"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <ArgoCDIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/argo-rollouts"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <ArgoRolloutsIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/fluxcd"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <FluxCDIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/karpenter"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <KarpenterIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/kyverno"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <KyvernoIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/trivy"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <TrivyIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/datadog"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <DatadogIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/cloudflare"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <CloudflareIntegration />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/integrations/knowledge-sources"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <TribalKnowledgeSettings />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/audit-log"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={['org_admin']}>
                  <AuditLog />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/cloud-integrations"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <CloudIntegrations />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/cluster-progress/:clusterId"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <EKSClusterProgress />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflows"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowList />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflows/suggestions"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowSuggestions />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflows/new"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <WorkflowBuilder />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflows/:id/edit"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ADMIN_ROLES]}>
                  <WorkflowBuilder />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflows/:id"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowDetail />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflows/:id/executions/:execId"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowExecutionDetail />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflows/:id/executions/:execId/rca/:stepId"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowRCADetail />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflow-approvals"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowApprovals />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflow-executions"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowExecutions />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflow-requests"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowRequests />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflows/request"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute>
                  <WorkflowRequestChat />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/workflow-observability"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <WorkflowObservability />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/api-keys"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={[...ALL_ROLES]}>
                  <APIKeys />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />
          <Route
            path="/billing"
            element={
              <AuthenticatedLayout>
                <ProtectedRoute allowedRoles={['org_admin']}>
                  <Billing />
                </ProtectedRoute>
              </AuthenticatedLayout>
            }
          />

          <Route path="/" element={<Navigate to="/home" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
